import type { AxiosRequestConfig, AxiosResponse } from "axios";

import axiosInstance from "../axiosConfig";

// eslint-disable-next-line no-restricted-syntax
interface Request {
  url: string;
  /**
   * The request payload.
   *
   * ⚠️ It is `body`, NOT `data` — but axios calls the same thing `data` both in
   * `axios.post(url, data)` and in `response.data`, so `data:` is the natural
   * thing to reach for and six call sites did exactly that. Because this
   * interface has an index signature, `data:` type-checks, is never read, and
   * the request goes out EMPTY with a 200 response and no error anywhere. The
   * signup and onboarding carrier pickers silently saved nothing from Sep 2025
   * until Aug 2026 because of it.
   *
   * `data` is now accepted as an alias so the mistake cannot cost anything.
   */
  body?: unknown;
  /** @deprecated Alias for `body`. Prefer `body`. */
  data?: unknown;
  auth?: boolean;
  options?: AxiosRequestConfig | undefined;
  [x: string]: unknown;
}

const del = async <T>({
  url,
  body: bodyArg,
  data: dataArg, // alias — see the note on Request.body
  auth = true, // Default to true
  options = {},
}: Request): Promise<AxiosResponse<T>> =>
  await axiosInstance.delete<T>(url, {
    data: bodyArg ?? dataArg,
    ...options,
    ...(auth === false && { headers: { ...options.headers, 'x-skip-auth': 'true' } })
  });

const get = async <T>({
  url,
  auth = true, // Default to true
  options = {},
}: Request): Promise<AxiosResponse<T>> => {
  return await axiosInstance.get<T>(url, {
    ...options,
    ...(auth === false && { headers: { ...options.headers, 'x-skip-auth': 'true' } })
  });
};

const post = async <T>({
  url,
  body,
  data, // alias — see the note on Request.body
  auth = true, // Default to true
  options = {},
}: Request): Promise<AxiosResponse<T>> => {
  return await axiosInstance.post<T>(url, body ?? data, {
    ...options,
    ...(auth === false && { headers: { ...options.headers, 'x-skip-auth': 'true' } })
  });
};

const patch = async <T>({
  url,
  body,
  data, // alias — see the note on Request.body
  auth = true,
  options = {},
}: Request): Promise<AxiosResponse<T>> =>
  await axiosInstance.patch<T>(url, body ?? data, {
    ...options,
    ...(auth === false && { headers: { ...options.headers, 'x-skip-auth': 'true' } })
  });

const put = async <T>({
  url,
  body,
  data, // alias — see the note on Request.body
  auth = true,
  options = {},
}: Request): Promise<AxiosResponse<T>> =>
  await axiosInstance.put<T>(url, body ?? data, {
    ...options,
    ...(auth === false && { headers: { ...options.headers, 'x-skip-auth': 'true' } })
  });

const api = {
  delete: del,
  get,
  patch,
  post,
  put,
};

export const runtime = "nodejs";

export default api;
